/**
 * Site Content API Client
 *
 * Handles CRUD operations for editable site content sections.
 * GET operations are public (no auth needed for public pages).
 * UPDATE operations require portal_director or portal_system_admin.
 */

const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

// Cache for public content (keyed by section ID)
const contentCache = {};
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an API request.
 * Public reads use /site-content-public (no auth).
 * Admin writes use /site-content (Cognito auth required).
 */
const apiRequest = async (operation, data = {}, requireAuth = false) => {
  const headers = {
    'Content-Type': 'application/json'
  };

  // Use authenticated endpoint for writes, public endpoint for reads
  const endpoint = requireAuth ? 'site-content' : 'site-content-public';

  if (requireAuth) {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Not authenticated. Please log in again.');
    }
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ operation, data })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * Get content for a specific section.
 * Public - no auth required.
 * Returns null if section has no custom content (use default).
 */
export const getSiteContent = async (sectionId) => {
  // Check cache
  const cached = contentCache[sectionId];
  if (cached && (Date.now() - cached.timestamp < CACHE_TTL)) {
    return cached.data;
  }

  try {
    const result = await apiRequest('get', { id: sectionId });
    const content = result.content ? result.content.content : null;

    // Cache the result
    contentCache[sectionId] = { data: content, timestamp: Date.now() };

    return content;
  } catch (error) {
    console.error(`Error fetching site content for ${sectionId}:`, error);
    return null; // Fall back to default content
  }
};

/**
 * List all content sections.
 * Public - no auth required.
 */
export const listSiteContent = async () => {
  try {
    const result = await apiRequest('list');
    return result.contents || [];
  } catch (error) {
    console.error('Error listing site content:', error);
    return [];
  }
};

/**
 * Update content for a section.
 * Requires portal_director or portal_system_admin.
 */
export const updateSiteContent = async (sectionId, content) => {
  const result = await apiRequest('update', { id: sectionId, content }, true);

  // Invalidate cache for this section
  delete contentCache[sectionId];

  return result;
};

/**
 * Clear all cached content.
 */
export const clearContentCache = () => {
  Object.keys(contentCache).forEach(key => delete contentCache[key]);
};
